import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarHeart, CheckCircle2, MessageCircle } from "lucide-react";
import { SITE, TREATMENTS } from "@/lib/site";
import { useSEO } from "@/lib/seo";
import SectionHeading from "@/components/SectionHeading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookAppointment() {
  useSEO({
    title: "Book Appointment",
    description: "Book a consultation with Dr. Anita Chaudhari at Nari Arogya, Kothrud, Pune. In-person or online.",
    path: "/book",
  });

  const [form, setForm] = useState({
    name: "", phone: "", email: "", age: "", concern: TREATMENTS[0].title,
    preferred_date: "", preferred_time: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.concern) {
      toast.error("Name, phone and concern are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/appointments`, form);
      toast.success("Appointment request received! We'll confirm shortly.");
      setDone(true);
    } catch (err) {
      toast.error("Booking failed. Please try WhatsApp or call.");
    } finally {
      setSubmitting(false);
    }
  };

  const waLink = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Hello, I'd like to book an appointment at Nari Arogya.")}`;

  if (done) {
    return (
      <section className="pt-40 pb-24">
        <div className="na-container max-w-2xl text-center">
          <div className="w-20 h-20 rounded-full bg-[color:var(--na-teal)]/10 text-[color:var(--na-teal)] flex items-center justify-center mx-auto">
            <CheckCircle2 size={44} />
          </div>
          <h1 className="mt-6 text-4xl sm:text-5xl">Thank you!</h1>
          <p className="mt-4 text-[color:var(--na-muted)] text-lg leading-relaxed">
            Your appointment request has been received. Our team will call or WhatsApp you within a few hours to confirm your slot.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <a href={waLink} target="_blank" rel="noreferrer" className="btn-primary">
              <MessageCircle size={16} /> WhatsApp Us
            </a>
            <button onClick={() => { setDone(false); setForm({ name: "", phone: "", email: "", age: "", concern: TREATMENTS[0].title, preferred_date: "", preferred_time: "", message: "" }); }} className="btn-outline">
              Book Another
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="pt-32 pb-14 lotus-bg" data-testid="book-hero">
        <div className="na-container text-center">
          <SectionHeading
            eyebrow="Consultation"
            title="Book your appointment"
            subtitle="Personalized Ayurvedic care starts with a conversation. Fill this form and we'll confirm your slot."
            align="center"
          />
        </div>
      </section>

      <section className="section">
        <div className="na-container grid gap-10 lg:grid-cols-[1.5fr_1fr]">
          <form onSubmit={handleSubmit} className="na-card p-8 md:p-10" data-testid="appointment-form">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Full Name *">
                <input name="name" value={form.name} onChange={handleChange} className={inputCls} required data-testid="book-name" />
              </Field>
              <Field label="Phone *">
                <input name="phone" value={form.phone} onChange={handleChange} className={inputCls} required data-testid="book-phone" />
              </Field>
              <Field label="Email">
                <input name="email" type="email" value={form.email} onChange={handleChange} className={inputCls} data-testid="book-email" />
              </Field>
              <Field label="Age">
                <input name="age" value={form.age} onChange={handleChange} className={inputCls} data-testid="book-age" />
              </Field>
              <Field label="Primary Concern *" full>
                <select name="concern" value={form.concern} onChange={handleChange} className={inputCls} required data-testid="book-concern">
                  {TREATMENTS.map((t) => (
                    <option key={t.slug} value={t.title}>{t.title}</option>
                  ))}
                  <option value="Other / General Consultation">Other / General Consultation</option>
                </select>
              </Field>
              <Field label="Preferred Date">
                <input name="preferred_date" type="date" value={form.preferred_date} onChange={handleChange} className={inputCls} data-testid="book-date" />
              </Field>
              <Field label="Preferred Time">
                <select name="preferred_time" value={form.preferred_time} onChange={handleChange} className={inputCls} data-testid="book-time">
                  <option value="">Select</option>
                  <option>Morning (10 AM – 1:30 PM)</option>
                  <option>Evening (5 PM – 8:30 PM)</option>
                </select>
              </Field>
              <Field label="Message" full>
                <textarea name="message" value={form.message} onChange={handleChange} rows={4} className={`${inputCls} resize-none`} placeholder="Briefly describe your health goals or concerns" data-testid="book-message" />
              </Field>
            </div>
            <button type="submit" disabled={submitting} className="btn-primary w-full mt-6 justify-center disabled:opacity-60" data-testid="book-submit">
              {submitting ? "Submitting..." : (<><CalendarHeart size={16} /> Request Appointment</>)}
            </button>
            <p className="mt-3 text-xs text-[color:var(--na-muted)] text-center">
              We'll confirm your slot via call or WhatsApp within a few hours.
            </p>
          </form>

          <aside className="space-y-4">
            <div className="na-card p-7">
              <h3 className="text-2xl">Consultation Details</h3>
              <ul className="mt-4 space-y-3 text-sm text-[color:var(--na-muted)]">
                <li className="flex gap-3"><CheckCircle2 size={18} className="text-[color:var(--na-teal)] shrink-0 mt-0.5" /> 45–60 minute first consultation</li>
                <li className="flex gap-3"><CheckCircle2 size={18} className="text-[color:var(--na-teal)] shrink-0 mt-0.5" /> Ayurvedic + modern assessment</li>
                <li className="flex gap-3"><CheckCircle2 size={18} className="text-[color:var(--na-teal)] shrink-0 mt-0.5" /> Written personalized care plan</li>
                <li className="flex gap-3"><CheckCircle2 size={18} className="text-[color:var(--na-teal)] shrink-0 mt-0.5" /> In-person or online available</li>
              </ul>
            </div>

            <div className="na-card p-7 bg-[color:var(--na-teal)] text-white">
              <h3 className="text-2xl text-white">Prefer to chat first?</h3>
              <p className="mt-2 text-sm text-white/85">Message us on WhatsApp — we'll help you decide the best next step.</p>
              <a href={waLink} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 bg-white text-[color:var(--na-text)] rounded-full px-5 py-2.5 font-semibold hover:bg-[color:var(--na-gold)] hover:text-white transition">
                <MessageCircle size={16} /> WhatsApp Us
              </a>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}

const inputCls =
  "w-full px-4 py-3 rounded-2xl bg-[color:var(--na-section)] border border-[color:var(--na-border)] focus:border-[color:var(--na-teal)] focus:outline-none focus:ring-2 focus:ring-[color:var(--na-teal)]/20 text-sm";

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-xs uppercase tracking-widest text-[color:var(--na-muted)] font-semibold">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
